import { IPublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";
import { Module } from "./types";

/** Extra options accepted by every strategy. */
export interface OpenProductOptions {
  /**
   * Original URL the user wanted to land on (passed by `/launch` from a
   * legacy bookmark). When set, forwarded to the product as `return_to`
   * in the SSO fragment so the product can navigate the user there after
   * creating its local session.
   */
  returnTo?: string;
}

/**
 * A strategy that opens a product card. Receives the MSAL instance so SSO
 * variants can acquire a token before redirecting. Strategies that don't
 * need MSAL (e.g. {@link openProductDirect}) simply ignore the parameter.
 */
export type OpenProductStrategy = (
  mod: Module,
  msalInstance: IPublicClientApplication,
  options?: OpenProductOptions
) => void | Promise<void>;

/**
 * Resolves `mod.url` against the current page origin and returns whether
 * the target lives on the same origin. Relative URLs (e.g. `/tech`) are
 * always same-origin; absolute URLs are compared against
 * `window.location.origin`. Same-origin products share the MSAL session
 * and the B2C cookie in the browser, so they don't need the SSO token
 * handoff — the product runs its own auth via ssoSilent and gets the
 * session for free.
 */
const isSameOrigin = (url: string): boolean => {
  try {
    const resolved = new URL(url, window.location.origin);

    return resolved.origin === window.location.origin;
  } catch {
    return false;
  }
};

/** Build the target URL for the direct strategy, appending return_to when set. */
const buildDirectTarget = (mod: Module, options?: OpenProductOptions): string => {
  if (!options?.returnTo) return mod.url ?? "";

  const separator = mod.url?.includes("?") ? "&" : "?";

  return `${mod.url}${separator}return_to=${encodeURIComponent(options.returnTo)}`;
};

/**
 * Direct strategy. Opens the product URL in a new tab and lets the product
 * handle its own login. Used for products that live on the same origin as
 * Identity (they inherit the MSAL session via the shared B2C cookie).
 */
export const openProductDirect: OpenProductStrategy = (mod, _msalInstance, options) => {
  if (!mod.url) return;
  window.open(buildDirectTarget(mod, options));
};

/**
 * Strip trailing slashes from a base URL so a path can be appended without
 * producing a double slash. The product URL is often stored with a trailing
 * `/` (e.g. `https://test.rastrin.app/`), which would otherwise yield
 * `https://test.rastrin.app//auth/sso`.
 */
const trimTrailingSlash = (url: string): string => url.replace(/\/+$/, "");

/** Coerce a possibly-null Date to "seconds until expiry", non-negative. */
const secondsUntil = (expiresOn: Date | null): number => {
  if (!expiresOn) return 0;

  return Math.max(0, Math.floor((expiresOn.getTime() - Date.now()) / 1000));
};

/**
 * SSO strategy. Used for cross-origin products that can't read the
 * Identity's MSAL session from the browser. Acquires a fresh B2C token
 * silently, then sends the user to `${mod.url}/auth/sso` **in a new tab**
 * with the id_token and access_token in the URL fragment.
 *
 * The new tab is opened synchronously up front — while we are still inside
 * the user-gesture window — so the popup blocker stays out of the way.
 * The tab is updated to the final URL only after `acquireTokenSilent`
 * resolves; until then the user sees a blank tab (typically a few ms).
 *
 * Falls the new tab back to the bare product URL when there is no active
 * MSAL account or interaction is required, so the click is never a dead
 * end.
 */
export const openProductSso: OpenProductStrategy = async (mod, msalInstance, options) => {
  if (!mod.url) return;

  // Open the tab synchronously — popup blocker is strict about post-await
  // window.open calls.
  const newTab = window.open("", "_blank");

  if (!newTab) {
    // eslint-disable-next-line no-console
    console.warn("openProductSso: popup blocked");

    return;
  }

  const account = msalInstance.getActiveAccount();

  if (!account) {
    newTab.location.href = buildDirectTarget(mod, options);

    return;
  }

  try {
    // OIDC scopes refresh the ID token, which already carries every claim a
    // legacy product needs (sub, email, name, extension_TenantId,
    // extension_Modules). Under the shared-App-Reg model, every product
    // validates the same audience (the clientId) — no per-product scope.
    // forceRefresh bypasses any cached id_token and re-mints one from the
    // refresh token bound to `account`. Without it, acquireTokenSilent can
    // return a stale id_token left in the cache by a previous user after an
    // account switch — the product would then receive the wrong identity.
    const result = await msalInstance.acquireTokenSilent({
      scopes: ["openid", "profile"],
      account,
      forceRefresh: true
    });

    const fragment = new URLSearchParams({
      id_token: result.idToken,
      access_token: result.accessToken,
      token_type: "Bearer",
      expires_in: String(secondsUntil(result.expiresOn))
    });

    if (options?.returnTo) {
      fragment.set("return_to", options.returnTo);
    }

    newTab.location.href = `${trimTrailingSlash(mod.url)}/auth/sso#${fragment.toString()}`;
  } catch (err) {
    // InteractionRequiredAuthError means the session can't be refreshed
    // silently. Falling back to the bare URL lets the product trigger its
    // own login flow, which sends the user back to Identity if needed.
    if (!(err instanceof InteractionRequiredAuthError)) {
      // eslint-disable-next-line no-console
      console.error("openProductSso failed; falling back to direct open", err);
    }
    newTab.location.href = buildDirectTarget(mod, options);
  }
};

/**
 * Cross-origin products that run their OWN login and must NOT receive the
 * Identity JWT fragment — the launcher opens their bare URL instead of
 * appending `/auth/sso#id_token=...`.
 *
 * - `backoffice` — TSplus Web Portal. TSplus has its own login page and does
 *   not consume the Identity token, so the card opens the URL directly.
 *   (Silent SSO into TSplus is planned separately.)
 */
const NATIVE_SSO_MODULE_IDS: ReadonlySet<string> = new Set(["backoffice"]);

/**
 * Entry point used by the launcher and `/launch`. Routes per product:
 *  - **Same-origin** (e.g. `/tech`, `/dispatch` served by the suite's main
 *    domain) → {@link openProductDirect}. The product inherits the MSAL/B2C
 *    session via the browser's cookie storage and authenticates on its own.
 *  - **Module in {@link NATIVE_SSO_MODULE_IDS}** → {@link openProductDirect}.
 *    Escape hatch for cross-origin products with their own login (e.g. TSplus),
 *    which would ignore or 404 on a JWT fragment.
 *  - **Everything else cross-origin** (e.g. `https://maxmohr.rastrin.app`) →
 *    {@link openProductSso}. The MSAL session isn't accessible across origins,
 *    so Identity hands the token over via the URL fragment.
 *
 * Both branches open the destination in a new tab. Callers must not call the
 * underlying strategies directly — always go through this entry point so the
 * routing applies.
 */
export const openProduct: OpenProductStrategy = (mod, msalInstance, options) => {
  if (!mod.url) return undefined;

  const usesDirectStrategy = isSameOrigin(mod.url)
    || NATIVE_SSO_MODULE_IDS.has(mod.id.toLowerCase());
  const strategy = usesDirectStrategy ? openProductDirect : openProductSso;

  return strategy(mod, msalInstance, options);
};
